import React from 'react';
import { motion } from 'framer-motion';
import { Cookie, Settings, Shield, Info, BarChart, Users, Megaphone, AlertCircle } from 'lucide-react';

const CookiePolicy: React.FC = () => {
  const cookieTypes = [
    {
      icon: Shield,
      title: "Essential Cookies",
      required: true,
      description: "Required for basic site functionality",
      examples: [
        "User authentication and security",
        "Session management",
        "Load balancing",
        "Security tokens"
      ]
    },
    {
      icon: BarChart,
      title: "Analytics Cookies",
      required: false,
      description: "Help us understand how visitors use our site",
      examples: [
        "Google Analytics tracking",
        "Page view statistics",
        "User behavior patterns",
        "Performance monitoring"
      ]
    },
    {
      icon: Users,
      title: "Functionality Cookies",
      required: false,
      description: "Remember your preferences and settings",
      examples: [
        "Language preferences",
        "Theme settings",
        "Favorite teams",
        "Display preferences"
      ]
    },
    {
      icon: Megaphone,
      title: "Marketing Cookies",
      required: false,
      description: "Used to deliver relevant advertisements",
      examples: [
        "Targeted advertising",
        "Ad campaign effectiveness",
        "Social media integration",
        "Remarketing pixels"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <Cookie className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Cookie Policy</h1>
            <p className="text-gray-400">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          {/* Introduction */}
          <div className="bg-gray-800/50 rounded-lg p-6 mb-8">
            <p className="text-gray-300 leading-relaxed mb-4">
              The Sports Machine Network uses cookies and similar tracking technologies to enhance your browsing experience,
              analyze site traffic, and personalize content. This Cookie Policy explains what cookies are, how we use them,
              and how you can manage your cookie preferences.
            </p>
            <div className="flex items-start space-x-2 text-blue-400">
              <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                Cookies are small text files stored on your device that help us remember your preferences and improve your experience.
              </p>
            </div>
          </div>

          {/* Cookie Types */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white mb-4">Types of Cookies We Use</h2>
            {cookieTypes.map((cookie, index) => {
              const Icon = cookie.icon;
              return (
                <motion.div
                  key={cookie.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-800/30 rounded-lg p-6 border border-gray-700"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className={`w-10 h-10 ${cookie.required ? 'bg-green-500/10' : 'bg-primary/10'} rounded-lg flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${cookie.required ? 'text-green-400' : 'text-primary'}`} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-xl font-semibold text-white">{cookie.title}</h3>
                        {cookie.required && (
                          <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded-full">
                            Required
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 mb-3">{cookie.description}</p>
                      <ul className="space-y-1">
                        {cookie.examples.map((example, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className={`${cookie.required ? 'text-green-400' : 'text-primary'} mr-2`}>•</span>
                            <span className="text-gray-300 text-sm">{example}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Managing Cookies */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-12 bg-gray-800/50 rounded-lg p-6"
          >
            <div className="flex items-center space-x-3 mb-4">
              <Settings className="w-5 h-5 text-primary" />
              <h3 className="text-xl font-semibold text-white">Managing Your Cookie Preferences</h3>
            </div>
            <div className="space-y-4 text-gray-300">
              <p>
                You can control and manage cookies in various ways. Please note that removing or blocking cookies
                may impact your user experience and some functionality may no longer be available.
              </p>
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-white mb-1">Browser Settings</h4>
                  <p className="text-sm">
                    Most browsers allow you to control cookies through their settings. You can set your browser to
                    notify you when you receive a cookie or to reject cookies altogether.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Third-Party Tools</h4>
                  <p className="text-sm">
                    You can opt out of third-party advertising cookies through platforms like:
                  </p>
                  <ul className="mt-2 space-y-1 text-sm">
                    <li>• Google Ad Settings</li>
                    <li>• Facebook Ad Preferences</li>
                    <li>• Digital Advertising Alliance</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Third-Party Cookies Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 bg-amber-500/10 rounded-lg p-6 border border-amber-500/20"
          >
            <div className="flex items-center space-x-3 mb-3">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              <h3 className="text-lg font-semibold text-white">Third-Party Cookies</h3>
            </div>
            <p className="text-gray-300 text-sm">
              Please note that third parties (including advertising networks and providers of external services)
              may also use cookies over which we have no control. These cookies are likely to be analytical/performance
              cookies or targeting cookies.
            </p>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-8 bg-primary/10 rounded-lg p-6 border border-primary/20"
          >
            <h3 className="text-lg font-semibold text-white mb-3">Questions About Cookies?</h3>
            <p className="text-gray-300 mb-3">
              If you have any questions about our use of cookies, please contact us:
            </p>
            <a href="mailto:privacy@the-sports-machine.com" className="text-primary hover:text-primary-400">
              privacy@the-sports-machine.com
            </a>
          </motion.div>

          {/* Footer Note */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              By continuing to use The Sports Machine Network, you consent to our use of cookies as described
              in this Cookie Policy. We may update this policy from time to time to reflect changes in our practices
              or for legal, operational, or regulatory reasons.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicy;